import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sendRequest } from '../utils/dateHelpers';
import {
  BILLING_SETUP_CHANGED_EVENT,
  resolveSetupProgressState
} from '../utils/setupProgress';

const INTEGRATIONS_URL = '/settings/integrations';
const PLAN_CONFIGURATION_URL =
  'https://panel.gitdotme.com/settings/plan-configuration';
const CARD_MANAGEMENT_URL =
  'https://panel.gitdotme.com/settings/card-management';

const ACTIVE_RING_STYLE = {
  backgroundImage:
    'linear-gradient(120deg, rgba(37,211,102,0.98) 0%, rgba(37,211,102,0.98) 38%, rgba(255,255,255,0.98) 50%, rgba(37,211,102,0.98) 64%, rgba(37,211,102,0.98) 100%)',
  backgroundSize: '220% 100%'
};

const STEP_THEMES = {
  completed: {
    badgeClassName:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200',
    cardClassName:
      'border-emerald-200/80 bg-white/90 shadow-[0_18px_50px_-30px_rgba(16,185,129,0.5)] dark:border-emerald-500/20 dark:bg-white/5',
    iconClassName:
      'border-emerald-200 bg-emerald-500 text-white dark:border-emerald-400/30',
    labelClassName: 'text-slate-900 dark:text-white',
    descriptionClassName: 'text-emerald-700 dark:text-emerald-200',
    actionClassName:
      'text-emerald-700 group-hover:text-emerald-800 dark:text-emerald-200 dark:group-hover:text-emerald-100'
  },
  active: {
    badgeClassName:
      'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200',
    cardClassName:
      'border-sky-200/80 bg-white shadow-[0_12px_30px_-20px_rgba(37,211,102,0.42)] dark:border-sky-400/30 dark:bg-slate-950/70 dark:shadow-[0_14px_34px_-22px_rgba(37,211,102,0.45)]',
    iconClassName:
      'border-sky-200 bg-sky-500 text-white dark:border-sky-400/30',
    labelClassName: 'text-slate-950 dark:text-white',
    descriptionClassName: 'text-sky-700 dark:text-sky-200',
    actionClassName:
      'text-sky-700 group-hover:text-sky-800 dark:text-sky-200 dark:group-hover:text-sky-100'
  },
  upcoming: {
    badgeClassName:
      'border-slate-200 bg-white/80 text-slate-600 dark:border-slate-700 dark:bg-white/5 dark:text-slate-300',
    cardClassName:
      'border-slate-200/80 bg-white/75 hover:border-slate-300 dark:border-slate-700/80 dark:bg-white/[0.03] dark:hover:border-slate-500',
    iconClassName:
      'border-slate-200 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
    labelClassName: 'text-slate-800 dark:text-slate-100',
    descriptionClassName: 'text-slate-500 dark:text-slate-400',
    actionClassName:
      'text-slate-500 group-hover:text-slate-700 dark:text-slate-400 dark:group-hover:text-slate-200'
  }
};

const SetupProgressHeader = () => {
  const { user } = useSelector(state => state);
  const [setupState, setSetupState] = useState(() =>
    resolveSetupProgressState({ user })
  );
  const [hasVerifiedSetupState, setHasVerifiedSetupState] = useState(false);

  useEffect(() => {
    if (!hasVerifiedSetupState) {
      return;
    }

    const nextLocalState = resolveSetupProgressState({ user });

    setSetupState(previousState => {
      const nextState = {
        hasIntegration: nextLocalState.hasIntegration,
        planConfigured:
          previousState.planConfigured || nextLocalState.planConfigured,
        paymentMethodAdded:
          previousState.paymentMethodAdded || nextLocalState.paymentMethodAdded
      };

      return {
        ...nextState,
        hasCompletedRequiredSteps:
          nextState.hasIntegration &&
          nextState.planConfigured &&
          nextState.paymentMethodAdded
      };
    });
  }, [hasVerifiedSetupState, user]);

  const applyProfileResult = useCallback(
    profileResult => {
      setSetupState(
        resolveSetupProgressState({
          user,
          profileResponse: profileResult
        })
      );
      setHasVerifiedSetupState(true);
    },
    [user]
  );

  const fetchSetupState = useCallback(async () => {
    const profileResult = await sendRequest('/billing/profile', 'GET').catch(
      () => null
    );
    applyProfileResult(profileResult);
  }, [applyProfileResult]);

  useEffect(() => {
    let active = true;
    const fetchIfActive = async () => {
      const profileResult = await sendRequest('/billing/profile', 'GET').catch(
        () => null
      );
      if (active) applyProfileResult(profileResult);
    };

    fetchIfActive();

    return () => {
      active = false;
    };
  }, [applyProfileResult]);

  useEffect(() => {
    const handleBillingSetupChanged = event => {
      if (event.detail) {
        applyProfileResult(event.detail);
        return;
      }
      fetchSetupState();
    };
    window.addEventListener(
      BILLING_SETUP_CHANGED_EVENT,
      handleBillingSetupChanged
    );
    return () => {
      window.removeEventListener(
        BILLING_SETUP_CHANGED_EVENT,
        handleBillingSetupChanged
      );
    };
  }, [applyProfileResult, fetchSetupState]);

  const { hasIntegration, planConfigured, paymentMethodAdded } = setupState;

  const steps = [
    {
      key: 'integration',
      stepNumber: '01',
      label: 'Connect Git',
      description: 'Link your repository provider to start collecting commits.',
      completed: hasIntegration,
      url: INTEGRATIONS_URL
    },
    {
      key: 'plan',
      stepNumber: '02',
      label: 'Configure Plan',
      description: 'Choose the plan that matches your team and analysis needs.',
      completed: planConfigured,
      url: PLAN_CONFIGURATION_URL
    },
    {
      key: 'payment',
      stepNumber: '03',
      label: 'Add Payment Method',
      description: 'Finalize billing to unlock uninterrupted insights.',
      completed: paymentMethodAdded,
      url: CARD_MANAGEMENT_URL
    }
  ];

  if (!hasVerifiedSetupState || setupState.hasCompletedRequiredSteps) {
    return null;
  }

  const completedStepsCount = steps.filter(step => step.completed).length;
  const activeStepIndex = steps.findIndex(step => !step.completed);
  const activeStep = steps[activeStepIndex] || steps[steps.length - 1];
  const activeMobileButtonClassName =
    'relative z-10 flex min-w-0 items-center gap-2 rounded-full border border-sky-200 bg-white/95 px-2.5 py-1.5 backdrop-blur dark:border-sky-500/30 dark:bg-slate-900/92';

  return (
    <div className="flex min-w-0 items-center justify-center">
      <div className="relative rounded-full p-[2px] shadow-[0_0_24px_rgba(37,211,102,0.4)] xl:hidden">
        <div
          aria-hidden="true"
          className="setup-ring-shimmer absolute inset-0 rounded-full"
          style={ACTIVE_RING_STYLE}
        />
        <button
          type="button"
          onClick={() => {
            window.location.href = activeStep.url;
          }}
          className={activeMobileButtonClassName}
        >
          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-500 text-[11px] font-semibold text-white dark:border-sky-400/30">
            {completedStepsCount}/3
          </span>
          <span className="block max-w-[120px] truncate text-xs font-semibold text-slate-900 dark:text-white">
            {activeStep.label}
          </span>
        </button>
      </div>

      <div className="hidden min-w-0 items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-2 py-1.5 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/75 xl:flex">
        <div className="flex items-center gap-2 pl-1">
          <span className="rounded-full bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
            {completedStepsCount}/3
          </span>
        </div>

        <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />

        <div className="flex min-w-0 items-center gap-1.5">
          {steps.map((step, index) => {
            const isActive = index === activeStepIndex;
            const theme = step.completed
              ? STEP_THEMES.completed
              : isActive
                ? STEP_THEMES.active
                : STEP_THEMES.upcoming;
            const buttonClassName = `group flex min-w-0 items-center rounded-full border px-1.5 py-1.5 transition-colors ${
              isActive
                ? `relative z-10 gap-2 pr-3 ${theme.cardClassName}`
                : theme.cardClassName
            }`;

            return (
              <div
                key={step.key}
                className={isActive ? 'relative rounded-full p-[2px]' : ''}
              >
                {isActive && (
                  <div
                    aria-hidden="true"
                    className="setup-ring-shimmer absolute inset-0 rounded-full shadow-[0_0_24px_rgba(37,211,102,0.42)]"
                    style={ACTIVE_RING_STYLE}
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = step.url;
                  }}
                  className={buttonClassName}
                >
                  <span
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${theme.iconClassName}`}
                  >
                    {step.completed ? (
                      <svg
                        viewBox="0 0 20 20"
                        className="h-3.5 w-3.5"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 10.5 8.2 13.7 15 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      step.stepNumber
                    )}
                  </span>
                  {isActive && (
                    <span
                      className={`max-w-[220px] truncate text-xs font-semibold 2xl:max-w-[280px] ${theme.labelClassName}`}
                    >
                      {step.label}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SetupProgressHeader;
